/**
 * The slice SAM sees, with click-to-prompt on top of it.
 *
 * Deliberately a second, smaller canvas rather than extending `SliceCanvas`:
 * that component owns multi-overlay reading (several checked files, no
 * pointer interaction); this one owns a single prompt loop (one file, every
 * click matters). Sharing the normalization and voxel layout — not the
 * component — is what keeps the two views of "the same slice" actually the
 * same; see `samMath.ts`'s header for why.
 */
import * as React from "react";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";
import type { NiftiVolume } from "@/lib/nifti";
import { ORIENTATIONS, type SliceOrientation } from "@/viewer/SliceCanvas";
import { extractSlice, sliceDepth } from "./samMath";

export interface SamPoint {
  x: number;
  y: number;
  label: 0 | 1; // 1 = positive (left click), 0 = negative (shift+click)
}

export function PromptCanvas({
  vol,
  norm,
  orientation,
  index,
  onOrientationChange,
  onIndexChange,
  points,
  onAddPoint,
  maskDisplay,
  busy,
  className,
}: {
  vol: NiftiVolume | null;
  norm: Float32Array | null;
  orientation: SliceOrientation;
  index: number;
  onOrientationChange: (o: SliceOrientation) => void;
  onIndexChange: (i: number) => void;
  points: SamPoint[];
  onAddPoint: (p: SamPoint) => void;
  maskDisplay: Uint8Array | null;
  busy: boolean;
  className?: string;
}) {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const slice = React.useMemo(() => {
    if (!vol || !norm) return null;
    return extractSlice(vol, norm, orientation, index);
  }, [vol, norm, orientation, index]);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !slice) return;
    canvas.width = slice.width;
    canvas.height = slice.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const img = ctx.createImageData(slice.width, slice.height);
    for (let i = 0; i < slice.gray.length; i++) {
      const g = Math.round(Math.min(1, Math.max(0, slice.gray[i])) * 255);
      const p = i * 4;
      img.data[p] = g;
      img.data[p + 1] = g;
      img.data[p + 2] = g;
      img.data[p + 3] = 255;
      if (maskDisplay?.[i]) {
        // amber overlay, matching SliceCanvas's single-overlay default
        img.data[p] = img.data[p] * 0.5 + 255 * 0.5;
        img.data[p + 1] = img.data[p + 1] * 0.5 + 210 * 0.5;
        img.data[p + 2] = img.data[p + 2] * 0.5;
      }
    }
    ctx.putImageData(img, 0, 0);
    for (const pt of points) {
      ctx.beginPath();
      ctx.arc(pt.x, pt.y, Math.max(3, slice.width / 128), 0, Math.PI * 2);
      ctx.fillStyle = pt.label === 1 ? "#22c55e" : "#ef4444";
      ctx.strokeStyle = "white";
      ctx.lineWidth = 1;
      ctx.fill();
      ctx.stroke();
    }
  }, [slice, points, maskDisplay]);

  const handleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (busy || !slice) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * canvas.width);
    const y = Math.round(((e.clientY - rect.top) / rect.height) * canvas.height);
    onAddPoint({ x, y, label: e.shiftKey ? 0 : 1 });
  };

  const max = vol ? sliceDepth(vol, orientation) - 1 : 0;

  return (
    <div className={cn("flex h-full min-h-0 flex-col bg-ads-viewer", className)}>
      <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden">
        {!vol ? (
          <p className="text-[11px] text-neutral-500">Loading the slice…</p>
        ) : null}
        {busy ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/30">
            <Loader2 className="h-5 w-5 animate-spin text-white" />
          </div>
        ) : null}
        <canvas
          ref={canvasRef}
          onClick={handleClick}
          className={cn(
            "max-h-full max-w-full object-contain [image-rendering:pixelated]",
            !vol && "hidden",
            busy ? "cursor-wait" : "cursor-crosshair",
          )}
        />
      </div>
      <div className="flex shrink-0 flex-col gap-1 border-t border-ads-border bg-ads-surface px-3 py-1.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            {ORIENTATIONS.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => onOrientationChange(o.id)}
                className={cn(
                  "rounded px-2 py-0.5 text-[11px] transition",
                  o.id === orientation
                    ? "bg-ads-accent-soft font-medium text-ads-accent"
                    : "text-neutral-500 hover:bg-ads-hover",
                )}
              >
                {o.label}
              </button>
            ))}
          </div>
          <span className="text-[10.5px] text-neutral-500">
            click = include · shift-click = exclude
          </span>
        </div>
        <div className="flex items-center gap-2 pb-0.5">
          <input
            type="range"
            min={0}
            max={max}
            step={1}
            value={Math.min(index, max)}
            disabled={!vol}
            aria-label={`${orientation} slice`}
            onChange={(e) => onIndexChange(Number(e.target.value))}
            className="h-4 min-w-0 flex-1 cursor-pointer accent-ads-accent disabled:cursor-default disabled:opacity-40"
          />
          <span className="w-16 shrink-0 text-right text-[10.5px] tabular-nums text-neutral-500">
            {vol ? `${Math.min(index, max) + 1} / ${max + 1}` : "—"}
          </span>
        </div>
      </div>
    </div>
  );
}
